/**
 * Trigger Action Response Plans — five, one per signal the reviewer walkthrough
 * touches: truck brake temperature, loader hydraulic oil temperature, rock-drill
 * percussion pressure, battery isolation and charging-connector moisture.
 *
 * A TARP turns a number into a named level and a named owner. The owner column
 * is the point of the reviewer's feedback: the lower rungs belong to the
 * **operator** and the **Operating Supervisor** (technique, briefs, handover),
 * the upper rungs to **maintenance** (inspection, parts, stand-down) or to the
 * **Superintendent** when production has to be restricted.
 *
 * Each level carries its trigger twice: in words (`trigger`, for the page and
 * the procedure) and as a machine-readable `rules` entry per data basis, which
 * `shared/reliability/tarp.ts` evaluates. Levels are checked highest first.
 *
 * Provenance
 * - The 240 °C brake limit and the 85 °C hydraulic limit are the demo's
 *   (`METRIC_INFO`, `predict_risk`). The "4 events > 220 °C" rung is procedure
 *   SP-MT63-BRK-002 §1 as cited on alert AL-0917. The 8 % percussion drop is
 *   bulletin TB-2026-027's signature as cited on alert AL-0912.
 * - Every other threshold, window and action is proof-of-concept content —
 *   plausible, internally consistent, and to be replaced by the site's own
 *   TARPs.
 *
 * Data Schema layer — seed data.
 *
 * @see src/versions/v1/schema/operations.ts — `Tarp`, `TarpLevel`, `TarpRule`
 */

import type { Tarp } from '../../schema/operations.ts';

const GREEN = 'var(--good)';
const AMBER = 'var(--fair)';
const ORANGE = 'var(--poor)';
const RED = 'var(--major)';

/** Every TARP. */
export const TARPS: readonly Tarp[] = [
  {
    id: 'TARP-BRK-TEMP',
    title: 'Rear brake temperature — haul trucks on decline',
    metric: 'rear_brake_temp_c',
    class: 'truck',
    component: 'Retarder & service brakes',
    unit: '°C',
    limit: 240,
    levels: [
      {
        level: 'Normal',
        colour: GREEN,
        trigger: 'Every loaded descent below 220 °C.',
        actions: ['Descend the 1:7 in retarder at or under 14 km/h', 'Brake check at pre-start'],
        owner: 'Operator',
      },
      {
        level: 'Level 1',
        colour: AMBER,
        trigger: 'One reading above 220 °C in the last hour, or a daily mean above 200 °C.',
        actions: [
          'Operator drops a gear and engages the retarder earlier on the decline',
          'Reported at shift handover',
        ],
        owner: 'Operator',
        rules: {
          live: { kind: 'above', threshold: 220, count: 1, window: 20 },
          daily: { kind: 'above', threshold: 200, count: 1, window: 1 },
        },
      },
      {
        level: 'Level 2',
        colour: ORANGE,
        trigger: 'Four readings above 220 °C in three hours (SP-MT63-BRK-002 §1), or daily mean above 220 °C on two of the last three days.',
        actions: [
          'Operating Supervisor briefs the operator on retarder use before the next descent',
          'Mine Control watches decline speed on the FMS',
          'Reliability Engineer reviews the brake trend; inspection at the next crib',
        ],
        owner: 'Operating Supervisor',
        rules: {
          live: { kind: 'above', threshold: 220, count: 4, window: 60 },
          daily: { kind: 'above', threshold: 220, count: 2, window: 3 },
        },
      },
      {
        level: 'Level 3',
        colour: RED,
        trigger: 'Three readings above the 240 °C limit in three hours, or a daily mean above 240 °C.',
        actions: [
          'Superintendent restricts the truck to empty haul through the FMS until inspected',
          'Maintenance Planner books pad and disc inspection (MER-BRK-PAD-63)',
          'Operator stood off the decline haul until briefed',
          'IR cool-down before wheel-end work — LOTO-UG-003, second-person verify',
        ],
        owner: 'Superintendent',
        rules: {
          live: { kind: 'above', threshold: 240, count: 3, window: 60 },
          daily: { kind: 'above', threshold: 240, count: 1, window: 1 },
        },
      },
    ],
  },
  {
    id: 'TARP-HYD-TEMP',
    title: 'Hydraulic oil temperature — loaders',
    metric: 'hyd_oil_temp_c',
    class: 'lhd',
    component: 'Hydraulic pump & drive',
    unit: '°C',
    limit: 85,
    levels: [
      {
        level: 'Normal',
        colour: GREEN,
        trigger: 'Shift-end mean below 80 °C.',
        actions: ['Normal duty'],
        owner: 'Operator',
      },
      {
        level: 'Level 1',
        colour: AMBER,
        trigger: 'Shift-end mean above 80 °C.',
        actions: ['Idle cool-down at crib', 'No boom-overload holds at the face', 'Reported at shift handover'],
        owner: 'Operator',
        rules: { daily: { kind: 'above', threshold: 80, count: 1, window: 1 } },
      },
      {
        level: 'Level 2',
        colour: ORANGE,
        trigger: 'Above the 85 °C limit on any of the last three days.',
        actions: [
          'Operating Supervisor checks duty cycle and bucket technique with the crew',
          'Reliability Engineer requests an oil sample and a cooler and fan-drive check',
        ],
        owner: 'Operating Supervisor',
        rules: { daily: { kind: 'above', threshold: 85, count: 1, window: 3 } },
      },
      {
        level: 'Level 3',
        colour: RED,
        trigger: 'Above 85 °C on each of the last three days.',
        actions: [
          'Machine stood down at the next safe stop',
          'Pump-drive coupling and pump inspection (MER-HYD-CPL-210)',
          'Reliability Engineer opens an RCA',
        ],
        owner: 'Reliability Engineer',
        rules: { daily: { kind: 'above', threshold: 85, count: 3, window: 3 } },
      },
    ],
  },
  {
    id: 'TARP-PERC-PRESS',
    title: 'Percussion pressure at full collar — rock drills',
    metric: 'percussion_pressure_bar',
    class: 'jumbo',
    alsoClasses: ['longhole'],
    component: 'rock drill',
    unit: 'bar',
    limit: null,
    levels: [
      {
        level: 'Normal',
        colour: GREEN,
        trigger: 'Within 5 % of the start-of-window mean.',
        actions: ['Normal duty'],
        owner: 'Operator',
      },
      {
        level: 'Level 1',
        colour: AMBER,
        trigger: 'More than 5 % below the start-of-window mean.',
        actions: ['Operator checks flushing water and feed pressure at the face', 'Reported at shift handover'],
        owner: 'Operator',
        rules: { daily: { kind: 'drop-pct', pct: 5, baselineN: 3 } },
      },
      {
        level: 'Level 2',
        colour: ORANGE,
        trigger: 'More than 8 % below — the TB-2026-027 front-end wear signature.',
        actions: [
          'Reliability Engineer confirms against TB-2026-027 and checks accumulator pre-charge',
          'Front-end inspection at the next cut change',
        ],
        owner: 'Reliability Engineer',
        rules: { daily: { kind: 'drop-pct', pct: 8, baselineN: 3 } },
      },
      {
        level: 'Level 3',
        colour: RED,
        trigger: 'More than 12 % below.',
        actions: [
          'Maintenance Planner schedules the front-end exchange (MER-RD-FE-KIT) at the next cut change',
          'Rig restricted to rehab duty until exchanged',
        ],
        owner: 'Maintenance Planner',
        rules: { daily: { kind: 'drop-pct', pct: 12, baselineN: 3 } },
      },
    ],
  },
  {
    id: 'TARP-BAT-ISO',
    title: 'Battery isolation — battery-electric loaders',
    metric: 'battery_isolation',
    class: 'lhd',
    appliesTo: 'battery',
    component: 'Battery pack (LFP, 300 kWh)',
    unit: 'events',
    limit: null,
    levels: [
      {
        level: 'Normal',
        colour: GREEN,
        trigger: 'No battery warnings in three days.',
        actions: ['Normal duty and charging'],
        owner: 'Operator',
      },
      {
        level: 'Level 1',
        colour: AMBER,
        trigger: 'A cell temperature-spread warning in the last three days.',
        actions: ['No fast charge while the pack is above 45 °C', 'Reported at shift handover'],
        owner: 'Operator',
        rules: { events: { kind: 'event-count', codes: ['W-BAT-TEMP-SPREAD'], count: 1, windowDays: 3 } },
      },
      {
        level: 'Level 2',
        colour: ORANGE,
        trigger: 'A battery derate in the last three days.',
        actions: [
          'Reliability Engineer requests a remote pack review and an isolation test at the next charge',
          'Single-shift duty until reviewed',
        ],
        owner: 'Reliability Engineer',
        rules: { events: { kind: 'event-count', codes: ['W-BAT-DERATE'], count: 1, windowDays: 3 } },
      },
      {
        level: 'Level 3',
        colour: RED,
        trigger: 'An isolation fault in the last seven days.',
        actions: [
          'HV isolated, fast charge locked out; HV-authorised technician only',
          'Maintenance Planner schedules the module swap (MER-BAT-MOD)',
          'Mine Control removes the loader from the charge roster',
        ],
        owner: 'Superintendent',
        rules: { events: { kind: 'event-count', codes: ['E-BAT-ISO'], count: 1, windowDays: 7 } },
      },
    ],
  },
  {
    id: 'TARP-CHG-MOIST',
    title: 'Charging connector moisture — battery-electric loaders',
    metric: 'charge_handshake',
    class: 'lhd',
    appliesTo: 'battery',
    component: 'Charging interface',
    unit: 'events',
    limit: null,
    levels: [
      {
        level: 'Normal',
        colour: GREEN,
        trigger: 'No failed charge handshakes in three days.',
        actions: ['Cap the charge inlet before wash-down'],
        owner: 'Operator',
      },
      {
        level: 'Level 1',
        colour: AMBER,
        trigger: 'A failed charge handshake in the last three days.',
        actions: ['Operator dries and caps the connector after wash-down, retries once', 'Reported at shift handover'],
        owner: 'Operator',
        rules: { events: { kind: 'event-count', codes: ['W-CHG-HANDSHAKE'], count: 1, windowDays: 3 } },
      },
      {
        level: 'Level 2',
        colour: ORANGE,
        trigger: 'Three or more failed handshakes in three days.',
        actions: [
          'Operating Supervisor re-briefs the wash-down procedure — no high-pressure water on the inlet',
          'Mine Control logs each failed handshake against the charger bay',
        ],
        owner: 'Operating Supervisor',
        rules: { events: { kind: 'event-count', codes: ['W-CHG-HANDSHAKE'], count: 3, windowDays: 3 } },
      },
      {
        level: 'Level 3',
        colour: RED,
        trigger: 'A charge handshake fault in the last seven days.',
        actions: [
          'Maintenance Planner schedules the connector change (MER-CHG-INT)',
          'Loader held to single-shift duty until changed',
        ],
        owner: 'Maintenance Planner',
        rules: { events: { kind: 'event-count', codes: ['E-CHG-HANDSHAKE'], count: 1, windowDays: 7 } },
      },
    ],
  },
];

/** TARP by id. */
export const TARP_BY_ID: Readonly<Record<string, Tarp>> = Object.fromEntries(TARPS.map((t) => [t.id, t]));
